import { useMemo } from "react";

import {
    HorizontalContainer,
    VerticalContainer,
} from "@shared/designSystem/Containers";
import { GridTable, GridTableCell, GridTableRow, TableColumn } from "@shared/designSystem/Tables";
import { BoldText, SmallText, Text } from "@shared/designSystem/Texts";
import { SystemIcon } from "@shared/designSystem/SystemIcon";
import SearchBar from "@shared/designSystem/SearchBar";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { ColaboratorInfo } from "@models/Colaborators";
import { AuditChange } from "@services/AuditService";
import { CollaboratorCustomField } from "@services/CollaboratorCustomFieldsService";
import { useColaboradorAuditoriaViewModel } from "./ColaboradorAuditoriaViewModel";

interface ColaboradorAuditoriaScreenProps {
    orgID: string;
    colaborador?: Partial<ColaboratorInfo> | null;
    hideTitle?: boolean;
    customFields?: CollaboratorCustomField[];
}

const fechaHeader = (
    <div style={{ width: "100%", paddingLeft: designSystemStyles.paddingLg }}>
        <Text text={"**Fecha**".localized()} markdown={true} />
    </div>
);

const horaHeader = (
    <div style={{ width: "100%", paddingLeft: designSystemStyles.paddingSm }}>
        <Text text={"**Hora**".localized()} markdown={true} />
    </div>
);

const tipoHeader = (
    <div style={{ width: "100%", paddingLeft: designSystemStyles.paddingSm }}>
        <Text text={"**Tipo**".localized()} markdown={true} />
    </div>
);

const responsableHeader = (
    <div style={{ width: "100%", paddingLeft: designSystemStyles.paddingSm }}>
        <Text text={"**Responsable**".localized()} markdown={true} />
    </div>
);

const campoHeader = <Text text={"**Campo**".localized()} markdown={true} />;
const antesHeader = <Text text={"**Antes**".localized()} markdown={true} />;
const ahoraHeader = <Text text={"**Ahora**".localized()} markdown={true} />;


const columns: TableColumn[] = [
    { id: "fecha", label: fechaHeader, width: "13%" },
    { id: "hora", label: horaHeader, width: "8%" },
    { id: "tipo", label: tipoHeader, width: "12%" },
    { id: "responsable", label: responsableHeader, width: "17%" },
    { id: "campo", label: campoHeader, width: "16%" },
    { id: "antes", label: antesHeader, width: "17%" },
    { id: "ahora", label: ahoraHeader, width: "17%" },
];

const fieldLabelMap: Record<string, string> = {
    mobilephone: "Teléfono",
    phonenumber: "Teléfono",
    telefono: "Teléfono",
    phone: "Teléfono",
    email: "Email",
    name: "Nombre y Apellido",
    dni: "Identificación",
    identificacion: "Identificación",
    localidad: "Localidad",
    city: "Ciudad",
    address: "Domicilio",
    direccion: "Domicilio",
    documentid: "Identificación",
    documento: "Documento",
    empresa: "Empresa",
    legajo: "Legajo",
    birthdate: "Fecha de nacimiento",
    jobtitle: "Puesto",
};

const phoneFieldNames = new Set(["mobilephone", "phonenumber", "telefono", "phone"]);
const dateOnlyFieldNames = new Set(["birthdate"]);

const formatDateOnly = (value: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
};

const getCustomFieldID = (field: string) => {
    const normalizedField = field.trim();
    if (!normalizedField.toLowerCase().startsWith("customfields.")) {
        return null;
    }

    return normalizedField.split(".").slice(1).join(".");
};

const formatAuditChangeValue = (
    field: string,
    value: string | null | undefined,
    customFields: CollaboratorCustomField[],
) => {
    if (!value) {
        return null;
    }

    const sanitizedValue = value.trim();
    if (!sanitizedValue) {
        return null;
    }

    if (phoneFieldNames.has(field.trim().toLowerCase())) {
        const sanitizedPhoneValue = sanitizedValue.replace(/^whatsapp:/i, "").trim();

        if (!sanitizedPhoneValue) {
            return null;
        }

        return sanitizedPhoneValue.startsWith("+") ? sanitizedPhoneValue : `+${sanitizedPhoneValue}`;
    }

    const customFieldID = getCustomFieldID(field);
    const customField = customFieldID
        ? customFields.find((currentField) => currentField.id === customFieldID)
        : undefined;

    if (customField?.typeId === "date" || dateOnlyFieldNames.has(field.trim().toLowerCase())) {
        return formatDateOnly(sanitizedValue);
    }

    if (customField?.typeId === "boolean") {
        if (sanitizedValue.toLowerCase() === "true") {
            return "Sí".localized();
        }

        if (sanitizedValue.toLowerCase() === "false") {
            return "No".localized();
        }
    }

    if (customField?.typeId === "list") {
        return customField.values?.find((listValue) => listValue.id === sanitizedValue)?.label ?? sanitizedValue;
    }

    return sanitizedValue;
};

const getAuditFieldLabel = (field: string, customFields: CollaboratorCustomField[]) => {
    const normalizedField = field.trim().toLowerCase();

    if (normalizedField.startsWith("customfields.")) {
        const customFieldID = getCustomFieldID(field);
        const customField = customFieldID
            ? customFields.find((currentField) => currentField.id === customFieldID)
            : undefined;

        if (customField) {
            return customField.name;
        }

        return field
            .split(".")
            .slice(1)
            .join(".")
            .replace(/[-_]+/g, " ")
            .replace(/\b\w/g, (letter) => letter.toUpperCase());
    }

    return fieldLabelMap[normalizedField] ?? field;
};

const getChangePresentation = (
    change: AuditChange,
    customFields: CollaboratorCustomField[],
) => {
    const label = getAuditFieldLabel(change.field, customFields);
    const oldValue = formatAuditChangeValue(change.field, change.oldValue, customFields);
    const newValue = formatAuditChangeValue(change.field, change.newValue, customFields);

    if (!oldValue && newValue) {
        return {
            label: label.localized(),
            oldValue: "Sin valor".localized(),
            newValue,
        };
    }

    if (oldValue && !newValue) {
        return {
            label: label.localized(),
            oldValue,
            newValue: "Sin valor".localized(),
        };
    }

    if (oldValue && newValue) {
        return {
            label: label.localized(),
            oldValue,
            newValue,
        };
    }

    return {
        label: label.localized(),
        oldValue: "Se actualizó".localized(),
        newValue: "Se actualizó".localized(),
    };
};

const EmptyState = ({ text }: { text: string }) => (
    <VerticalContainer
        height="120px"
        alignment="center"
        justifyContent="center"
        backgroundColor={designSystemStyles.colorBackgroundWhite}
        borderRadius={designSystemStyles.cornerRadiusComponent}
    >
        <Text text={text} color={designSystemStyles.colorTextSecondary} alignment="center" />
    </VerticalContainer>
);

export default function ColaboradorAuditoriaScreen({
    orgID,
    colaborador,
    hideTitle = false,
    customFields = [],
}: ColaboradorAuditoriaScreenProps) {
    const viewModel = useColaboradorAuditoriaViewModel({ orgID, colaborador });
    const filteredRows = useMemo(() => {
        const searchQuery = viewModel.searchQuery.trim().toLowerCase();

        if (!searchQuery) {
            return viewModel.auditRows;
        }

        return viewModel.auditRows.filter((row) => {
            const fieldLabel = row.change
                ? getAuditFieldLabel(row.change.field, customFields).localized()
                : "";
            const searchableText = [
                row.tipo,
                row.tipo.localized(),
                row.responsable,
                fieldLabel,
            ].join(" ").toLowerCase();

            return searchableText.includes(searchQuery);
        });
    }, [customFields, viewModel.auditRows, viewModel.searchQuery]);

    return (
        <VerticalContainer
            height="100%"
            gap={designSystemStyles.gapLg}
            margin={designSystemStyles.paddingZero}
        >
            {!hideTitle ? (
                <HorizontalContainer gap={designSystemStyles.gapSm}>
                    <SystemIcon type="notes" color={designSystemStyles.colorTextSecondary} />
                    <BoldText text={"Auditoria".localized()} size="lg" />
                </HorizontalContainer>
            ) : null}

            {viewModel.error && (
                <SmallText text={viewModel.error} color={designSystemStyles.colorError} />
            )}

            <HorizontalContainer width="100%" gap={designSystemStyles.gapSm} alignment="center">
                <div style={{ width: "420px", maxWidth: "100%" }}>
                    <SearchBar
                        placeholder={"Buscar".localized()}
                        value={viewModel.searchQuery}
                        onChange={viewModel.setSearchQuery}
                    />
                </div>
            </HorizontalContainer>

            <GridTable columns={columns} minWidth="1080px" isLoading={viewModel.loading}>
                {filteredRows.length ? (
                    filteredRows.map((row) => {
                        const presentation = row.change
                            ? getChangePresentation(row.change, customFields)
                            : null;

                        return (
                            <GridTableRow key={row.id}>
                                <GridTableCell>
                                    <div style={{ width: "100%", paddingLeft: designSystemStyles.paddingLg }}>
                                        <Text text={row.fecha} truncate={true} />
                                    </div>
                                </GridTableCell>
                                <GridTableCell>
                                    <div style={{ width: "100%", paddingLeft: designSystemStyles.paddingSm }}>
                                        <Text text={row.hora} truncate={true} />
                                    </div>
                                </GridTableCell>
                                <GridTableCell>
                                    <div style={{ width: "100%", paddingLeft: designSystemStyles.paddingSm }}>
                                        <Text text={row.tipo.localized()} truncate={true} />
                                    </div>
                                </GridTableCell>
                                <GridTableCell>
                                    <div style={{ width: "100%", paddingLeft: designSystemStyles.paddingSm }}>
                                        <Text text={row.responsable} truncate={true} />
                                    </div>
                                </GridTableCell>
                                <GridTableCell>
                                    <Text text={presentation?.label ?? "—"} truncate={true} />
                                </GridTableCell>
                                <GridTableCell>
                                    <Text text={presentation?.oldValue ?? "—"} truncate={true} />
                                </GridTableCell>
                                <GridTableCell>
                                    {presentation ? (
                                        <BoldText text={presentation.newValue} size="sm" />
                                    ) : (
                                        <Text text="—" />
                                    )}
                                </GridTableCell>
                            </GridTableRow>
                        );
                    })
                ) : (
                    <EmptyState
                        text={
                            viewModel.error
                                ? "No hay registros para mostrar.".localized()
                                : "No hay registros de auditoria para este colaborador.".localized()
                        }
                    />
                )}
            </GridTable>
        </VerticalContainer>
    );
}
